<template>
    <div class="mt-4">
        <div v-if="haveItems">
            <div class="border-b pb-2 xs:mb-1">
                <div class="md:flex md:justify-between items-center">
                    <div v-if="haveItems">
                        <div class="flex-auto print:hidden">
                            <div class="flex flex-wrap">
                                <div class="text-xs sm:text-sm font-semibold mr-2">Go to:</div>
                                <template v-for="(tableKey, index) in tableOrder" :key="tableKey">
                                    <a @click.prevent="scrollTo(`scrollTo-${index}`)" class="align-middle text-xs sm:text-sm pr-2 font-semibold cursor-pointer" v-if="tables[tableKey].request">
                                        {{ tables[tableKey].title }}
                                    </a>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="flex-auto mt-2 md:mt-0 md:text-right">
                        <label for="customQuotesPeriod" class="leading-none self-center text-black mr-1">Period:</label>
                        <select
                            v-model="period"
                            @change="updatePeriods"
                            class="form-control form-control-sm form-control-fixed bg-white"
                            id="customQuotesPeriod"
                            aria-label="Select Period"
                        >
                            <template v-for="option in periods" :key="option.value">
                                <option :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                                    {{ option.label }}
                                </option>
                            </template>
                        </select>
                    </div>
                </div>
            </div>

            <div v-for="(tableKey, index) in tableOrder" :key="tableKey">
                <div v-if="tables[tableKey].request">
                    <div class="flex print:hidden">
                        <button @click="flipCharts(tableKey)" class="items-center text-xs lg:text-sm btn btn-sm text-blue-600 hover:text-blue-400 mb-2 mt-1 sm:mt-3 ml-auto">
                            <span class="mr-1">FlipCharts</span>
                            <i class="fas fa-chart-area"></i>
                        </button>
                    </div>

                    <div class="border-t-2 border-blue-600">
                        <div class="table-header" :id="`scrollTo-${index}`">
                            <strong>{{ tables[tableKey].title }}</strong>

                            <div class="ml-auto print:hidden">
                                <a :href="getAddLink(tableKey)" class="btn btn-xs btn-yellow">Add or Edit {{ tables[tableKey].title }}</a>
                            </div>
                        </div>

                        <data-table
                            :ref="tableKey"
                            @rows-loaded="rows => handleRowsLoaded(tableKey, rows)"
                            :default-request="tables[tableKey].request"
                            :default-columns="tables[tableKey].columns">
                        </data-table>

                    </div>
                </div>
            </div>
        </div>
        <alert-box class="mb-3" v-else>
            <template #body>
                No quotes have been added. Please click one of the buttons below to add a custom quote.
            </template>
        </alert-box>

        <div class="flex flex-wrap justify-center border-t pt-3 print:hidden">
            <a :href="getAddLink(tableKey)" class="btn btn-sm btn-blue-500 inline-block mx-1 mb-1" v-for="tableKey in tableOrder" :key="tableKey">
                Add {{ tables[tableKey].title }}
            </a>
        </div>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useFlipcharts } from '../DataTable/mixins/FlipCharts';
import { useAddBlankRows } from '../DataTable/mixins/AddBlankRows';

// Props
const props = defineProps({
    defaultTables: Object
});

const { flipcharts } = useFlipcharts();
const { addBlankRows } = useAddBlankRows();
const { appContext } = getCurrentInstance();

const tablesRows = ref([]);

const tables = ref(props.defaultTables);
const periods = ref([
    { label: 'Intraday', value: 'intraday' },
    { label: 'End-of-Day', value: 'eod' }
]);
const period = ref('intraday');
const haveItems = ref(false);
const tableOrder = ref(['future', 'option', 'forex', 'stock', 'futurespread', 'crypto']);

// Computed properties
const offset = computed(() => {
    const thead = document.querySelector('table.table thead');
    return thead.getBoundingClientRect().height * -1;
});

// Methods
const scrollTo = (element) => {
    const options = {
        offset: offset.value,
        container: document.body,
    };
    appContext.app.config.globalProperties.$scrollTo(`#${element}`, undefined, options);
};

const getAddLink = (section) => window.route('my-folder.custom-quotes.edit', { section });

function convertToEndOfDay(field) {
    return `daily${field[0].toUpperCase()}${field.slice(1)}`;
}

function convertToIntraday(field) {
    const fieldName = field.replace(/^daily/, '');
    return `${fieldName[0].toLowerCase()}${fieldName.slice(1)}`;
}

const handleRowsLoaded = (key, rows) => {
    tablesRows.value[key] = rows; // Store the rows in the reactive object
};

function flipCharts(tableKey) {
    if (tablesRows.value[tableKey]) {
        const symbols = tablesRows.value[tableKey].map(row => row.symbol);
        flipcharts(symbols);
    }
}

function convertField(field) {
    const dontConvert = ['contractName', 'contractMonthYear', 'symbol', 'symbolName', 'category', 'uscCategory', 'fullNameWithoutExchange', 'strikePrice', 'strike', 'symbolType', 'longSymbol', 'optionType', 'buttons'];
    if (dontConvert.includes(field)) return field;

    if (period.value === 'intraday') {
        return convertToIntraday(field);
    } else if (period.value === 'eod') {
        return convertToEndOfDay(field);
    }
}

function updatePeriods() {
    Object.keys(tables.value).map(tableName => {
        const table = tables.value[tableName];
        if (!table.request) return;

        const tableFields = table.request.fields.split(',');
        const newFields = tableFields.map(convertField).join();
        const newColumns = table.columns.map(column => {
            if (column.field === 'buttons') return column;
            return {
                label: column.label,
                field: convertField(column.field)
            };
        });

        tables.value[tableName].request.fields = newFields;
        tables.value[tableName].columns = newColumns;
    });
}

// Lifecycle hooks
onMounted(() => {
    Object.keys(tables.value).map(tableName => {
        const table = tables.value[tableName];
        if (table.request) {
            table.request.afterFunc = addBlankRows;
            haveItems.value = true;
        }
    });
});
</script>
